import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { Button, Input, Spin, Switch, message } from 'antd';
import { useNavigate, useSearchParams } from 'react-router-dom';
import klarityLogo from 'assets/logo/KlarityLogo.png';
import { getReviewData } from 'api/ppaOnboarding';
import { switchProvider, sendTeamMemberInvite, editStaff } from 'api/group';
import { getReviewGroupProviderIds, setReviewGroupProviderIds, clearReviewGroupProviderIds } from 'utils/localstore';
import { isValidUSPhoneV2 } from 'utils/form';
import UsPhoneInput from 'components/form/USPhoneInput';
import { PATH } from 'constants/path';
import { openManageSubscriptionModal } from 'utils/globalLayerControl';
import { IReviewData } from 'hooks/usePPAOnboardingReviewInfo';
import SwitchedProviderContext from './SwitchedProviderContext';
import useUnsavedCards from './useUnsavedCards';
import PracticeSetupTab from './components/PracticeSetupTab';
import ProfileTab from './components/ProfileTab';
import s from './s.module.less';

const TABS = ['Practice setup', 'Profile'];

const ReviewProviderProfilePage: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [providerIds, setProviderIds] = useState<number[]>([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [activeTab, setActiveTab] = useState(0);
    const [loading, setLoading] = useState(true);
    const [sending, setSending] = useState(false);
    const [reviewData, setReviewData] = useState<IReviewData | null>(null);
    const [isAdmin, setIsAdmin] = useState(false);
    const [memberId, setMemberId] = useState<number | null>(null);
    const [firstName, setFirstName] = useState('');
    const [lastName, setLastName] = useState('');
    const [phone, setPhone] = useState('');
    const [phoneError, setPhoneError] = useState('');
    const [switchedToken, setSwitchedToken] = useState<string | undefined>(undefined);
    const { onEditingChange, blockIfUnsaved, runIfSaved } = useUnsavedCards();

    useEffect(() => {
        const queryIds = searchParams.get('providerIds');
        if (queryIds) {
            const ids = queryIds.split(',').map(Number).filter(Boolean);
            if (ids.length) {
                setProviderIds(ids);
                setReviewGroupProviderIds(ids);
                return;
            }
        }
        const storedIds = getReviewGroupProviderIds();
        if (storedIds.length) {
            setProviderIds(storedIds);
        } else {
            navigate(-1);
        }
    }, []);

    const loadProviderData = useCallback(async (providerId: number) => {
        setLoading(true);
        setReviewData(null);
        try {
            const tokenRes = await switchProvider(providerId);
            const loginData = tokenRes?.data?.data;
            if (tokenRes?.data?.code !== 200 || !loginData?.token) {
                message.error(tokenRes?.data?.message || 'Failed to switch provider');
                return;
            }
            const { token, teamMemberId } = loginData;
            setSwitchedToken(token);
            setMemberId(teamMemberId ?? null);

            const res = await getReviewData(token);
            if (res?.data?.code === 200) {
                const { data } = res.data;
                setReviewData(data);
                setFirstName(data?.basicInfo?.firstName || '');
                setLastName(data?.basicInfo?.lastName || '');
                setPhone((data?.basicInfo?.tel || '').replace(/\D/g, ''));
                setPhoneError('');
            } else {
                message.error(res?.data?.message || 'Failed to load provider data');
            }
        } catch (error) {
            console.error(error);
            message.error('Failed to load provider data');
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        if (providerIds.length && providerIds[currentIndex]) {
            setActiveTab(0);
            loadProviderData(providerIds[currentIndex]);
        }
    }, [providerIds, currentIndex, loadProviderData]);

    const currentProviderId = providerIds[currentIndex];
    const isLast = currentIndex === providerIds.length - 1;
    const basicInfo = reviewData?.basicInfo;
    const providerName = `${basicInfo?.firstName || ''} ${basicInfo?.lastName || ''}`.trim();

    const handleExit = useCallback(() => {
        clearReviewGroupProviderIds();
        navigate(`${PATH.DASHBOARD}`);
    }, [navigate]);

    const handleBack = useCallback(() => {
        if (currentIndex > 0) {
            setCurrentIndex(currentIndex - 1);
        } else {
            handleExit();
        }
    }, [currentIndex, handleExit]);

    const handlePhoneChange = useCallback((val: string) => {
        setPhone(val);
        setPhoneError('');
    }, []);

    const handlePhoneBlur = useCallback(() => {
        if (phone && !isValidUSPhoneV2(phone)) {
            setPhoneError('Please enter a 10-digit phone number');
        }
    }, [phone]);

    const handleSendAndNext = useCallback(async () => {
        if (!currentProviderId || !memberId) return;
        // Send 只保存姓名/电话/admin，卡片里未保存的改动会被丢掉，先拦住
        if (blockIfUnsaved()) return;
        if (!firstName.trim() || !lastName.trim() || !phone.trim()) {
            message.error('Please fill in all required fields');
            return;
        }
        if (!isValidUSPhoneV2(phone)) {
            setPhoneError('Please enter a 10-digit phone number');
            return;
        }
        setSending(true);
        try {
            const editRes = await editStaff(memberId, {
                firstName: firstName.trim(),
                lastName: lastName.trim(),
                phoneNumber: phone.trim(),
                isAdmin,
            });
            if (editRes?.error) {
                message.error(editRes.error || 'Failed to save provider info');
                return;
            }

            const res = await sendTeamMemberInvite(memberId);
            if (res?.data?.code !== 200) {
                message.error(res?.data?.message || 'Failed to send invitation');
                return;
            }
            message.success('Invitation sent');

            if (isLast) {
                clearReviewGroupProviderIds();
                // Return to Settings > Team Management so the user can continue adding members.
                navigate(`${PATH.DASHBOARD}`);
                openManageSubscriptionModal('9');
            } else {
                setCurrentIndex(currentIndex + 1);
            }
        } catch (error) {
            console.error(error);
            message.error('Failed to send invitation');
        } finally {
            setSending(false);
        }
    }, [
        currentProviderId,
        memberId,
        firstName,
        lastName,
        phone,
        isAdmin,
        isLast,
        currentIndex,
        navigate,
        blockIfUnsaved,
    ]);

    const refreshProviderData = useCallback(async () => {
        if (!switchedToken) return;
        try {
            const res = await getReviewData(switchedToken);
            if (res?.data?.code === 200) {
                const { data } = res.data;
                setReviewData(data);
                setFirstName(data?.basicInfo?.firstName || '');
                setLastName(data?.basicInfo?.lastName || '');
                setPhone((data?.basicInfo?.tel || '').replace(/\D/g, ''));
                setPhoneError('');
            }
        } catch (error) {
            console.error(error);
        }
    }, [switchedToken]);

    const switchedProviderValue = useMemo(
        () => ({
            scheduleToken: switchedToken,
            providerId: currentProviderId,
            onRefresh: refreshProviderData,
            onEditingChange,
        }),
        [switchedToken, currentProviderId, refreshProviderData, onEditingChange],
    );

    const practiceSetupData = reviewData
        ? {
              firstName: basicInfo?.firstName,
              lastName: basicInfo?.lastName,
              credential: basicInfo?.credential,
              gender: basicInfo?.gender,
              services: reviewData.services,
              availability: reviewData.availability,
              reminderSettings: reviewData.calendarSetting,
          }
        : null;

    return (
        <div className={s.page}>
            <div className={s.topBar}>
                <img src={klarityLogo} alt="Klarity" className={s.logo} />
                <Button className={s.exitBtn} onClick={handleExit}>
                    Exit
                </Button>
            </div>

            <div className={s.scrollArea}>
                {providerIds.length > 1 && (
                    <div className={s.progressArea}>
                        <span className={s.progressLabel}>
                            {currentIndex + 1} of {providerIds.length}
                        </span>
                        <div className={s.progressTrack}>
                            <div
                                className={s.progressFill}
                                style={{ width: `${((currentIndex + 1) / providerIds.length) * 100}%` }}
                            />
                        </div>
                    </div>
                )}

                {loading || !switchedToken ? (
                    <div className={s.loadingArea}>
                        <Spin size="large" />
                    </div>
                ) : (
                    <>
                        <h2 className={s.title}>
                            Review <span className={s.titleName}>{providerName}</span>
                            {`'s profile`}
                        </h2>

                        <div className={s.basicInfoCard}>
                            <div className={s.formRow}>
                                <div className={s.inputGroup}>
                                    <span className={s.inputLabel}>
                                        First name <span className={s.required}>*</span>
                                    </span>
                                    <Input value={firstName} onChange={(e) => setFirstName(e.target.value)} />
                                </div>
                                <div className={s.inputGroup}>
                                    <span className={s.inputLabel}>
                                        Last name <span className={s.required}>*</span>
                                    </span>
                                    <Input value={lastName} onChange={(e) => setLastName(e.target.value)} />
                                </div>
                            </div>
                            <div className={s.formRow}>
                                <div className={s.inputGroup}>
                                    <span className={s.inputLabel}>
                                        Email <span className={s.required}>*</span>
                                    </span>
                                    <Input value={basicInfo?.email || ''} disabled />
                                </div>
                                <div className={s.inputGroup}>
                                    <span className={s.inputLabel}>
                                        Phone number <span className={s.required}>*</span>
                                    </span>
                                    <UsPhoneInput
                                        value={phone}
                                        onChange={(val) => handlePhoneChange(val as string)}
                                        onBlur={handlePhoneBlur}
                                        status={phoneError ? 'error' : undefined}
                                    />
                                    {phoneError && <span className={s.fieldError}>{phoneError}</span>}
                                </div>
                            </div>
                            <div className={s.toggleRow}>
                                <Switch checked={isAdmin} onChange={setIsAdmin} />
                                <div className={s.toggleContent}>
                                    <div className={s.toggleTitle}>Practice-wide admin access</div>
                                    <div className={s.toggleDesc}>
                                        Grants access to system configuration, user management, billing oversight, and
                                        practice-level settings. Overrides role-based permissions.
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className={s.tabs}>
                            {TABS.map((tab, i) => (
                                <div
                                    key={tab}
                                    className={`${s.tab} ${i === activeTab ? s.tabActive : ''}`}
                                    onClick={() => {
                                        if (i !== activeTab) runIfSaved(() => setActiveTab(i));
                                    }}
                                >
                                    <span className={s.tabLabel}>{tab}</span>
                                    <div className={i === activeTab ? s.tabBar : s.tabBarHidden} />
                                </div>
                            ))}
                        </div>

                        <SwitchedProviderContext.Provider value={switchedProviderValue}>
                            {activeTab === 0 ? (
                                <PracticeSetupTab key={currentProviderId} provider={practiceSetupData} />
                            ) : (
                                <ProfileTab key={currentProviderId} />
                            )}
                        </SwitchedProviderContext.Provider>
                    </>
                )}
            </div>

            <div className={s.footer}>
                <Button className={s.backBtn} onClick={handleBack}>
                    Back
                </Button>
                <Button
                    type="primary"
                    className={s.continueBtn}
                    onClick={handleSendAndNext}
                    loading={sending}
                    disabled={loading}
                >
                    {isLast ? 'Send invitation and finish' : 'Send invitation and review next provider'}
                </Button>
            </div>
        </div>
    );
};

export default ReviewProviderProfilePage;
